/**
 * Partner Portal — "Order Sample Kits" page (Feb 2026).
 *
 * Inventory dashboard + bulk-order Stripe checkout, in one screen.
 * Boxes come in multiples of six ($29/kit fixed). Stripe checkout is
 * minted server-side; success/cancel URLs bring the partner back
 * here with a query flag.
 */
import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Package, ShoppingCart, RefreshCw, DollarSign, Loader2,
  CheckCircle2, AlertTriangle, Info, ExternalLink,
} from "lucide-react";
import { PartnerShell } from "@/pages/partner/Dashboard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

const CENTS = (n) => `$${((n || 0) / 100).toFixed(2)}`;

export default function PartnerKitOrders() {
  const [params, setParams] = useSearchParams();
  const [dashboard, setDashboard] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [boxes, setBoxes] = useState(1);
  const [checkingOut, setCheckingOut] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [d, o] = await Promise.all([
        api.get("/partner/kit-inventory"),
        api.get("/partner/kit-orders"),
      ]);
      setDashboard(d.data);
      setOrders(o.data.items || []);
    } catch {
      toast.error("Couldn't load kit ordering data.");
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  // Feb 2026 — accept `?boxes=<N>&reorder=1` deep-link from the
  // partner dashboard's Low-Stock Nudge. Pre-fills the box count
  // and (when `reorder=1` is set) scrolls the order picker into
  // view + surfaces a toast so the admin/partner sees the intent.
  useEffect(() => {
    const boxParam = parseInt(params.get("boxes") || "", 10);
    if (Number.isFinite(boxParam) && boxParam >= 1 && boxParam <= 100) {
      setBoxes(boxParam);
    }
    if (params.get("reorder") === "1") {
      toast.info("We've pre-filled a recommended order to get you back above threshold.");
      setTimeout(() => {
        const el = document.querySelector("[data-testid='partner-kit-order-picker']");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 250);
    }
    if (params.get("boxes") || params.get("reorder")) {
      // Clear the query so a page refresh doesn't re-toast.
      const next = new URLSearchParams(params);
      next.delete("boxes");
      next.delete("reorder");
      setParams(next, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (params.get("paid")) {
      toast.success("Payment succeeded — order in the AWARE fulfilment queue.");
      setParams({});
      reload();
    } else if (params.get("cancelled")) {
      toast("Checkout cancelled — the order stays as pending.");
      setParams({});
    }
  }, [params]);

  const s = dashboard?.settings || {};
  const perBox = s.kits_per_box || 6;
  const kitsPerKit = s.partner_kit_price_cents || 2900;
  const kits = boxes * perBox;
  const total = kits * kitsPerKit;

  const invSnap = dashboard?.inventory || { available: 0, ordered: 0, used: 0 };
  const pending = dashboard?.pending_reimb || { count: 0, cents: 0 };
  const reimbursed = dashboard?.reimbursed  || { count: 0, cents: 0 };

  const cards = useMemo(() => ([
    { label: "Kits available",       value: invSnap.available, testid: "kit-card-available", tone: "bg-cyan-50 text-cyan-800 border-cyan-100" },
    { label: "Kits ordered (all-time)", value: invSnap.ordered, testid: "kit-card-ordered",  tone: "bg-slate-50 text-slate-800 border-slate-100" },
    { label: "Kits used",            value: invSnap.used,      testid: "kit-card-used",      tone: "bg-emerald-50 text-emerald-800 border-emerald-100" },
    { label: "Pending reimbursements", value: `${pending.count} · ${CENTS(pending.cents)}`, testid: "kit-card-pending",    tone: "bg-amber-50 text-amber-800 border-amber-100" },
    { label: "Reimbursed",           value: `${reimbursed.count} · ${CENTS(reimbursed.cents)}`, testid: "kit-card-reimbursed", tone: "bg-emerald-50 text-emerald-800 border-emerald-100" },
  ]), [invSnap, pending, reimbursed]);

  const checkout = async () => {
    setCheckingOut(true);
    try {
      const { data } = await api.post("/partner/kit-orders/checkout", { boxes });
      window.location.href = data.checkout_url;
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to start checkout");
      setCheckingOut(false);
    }
  };

  return (
    <PartnerShell active="order sample kits">
      <div className="p-6 space-y-5" data-testid="partner-kit-orders-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Package className="w-6 h-6" /> Order Sample Kits
          </h1>
          <p className="text-slate-600 text-sm mt-1">
            Bulk sample kits at $29 each · sealed boxes of six · one prepaid
            Express Post return label per kit · no separate freight charge.
          </p>
        </div>

        {loading ? (
          <div className="p-8 text-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
          </div>
        ) : (
          <>
            {/* Inventory summary */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {cards.map((c) => (
                <div
                  key={c.label} data-testid={c.testid}
                  className={`p-4 rounded-2xl border ${c.tone}`}
                >
                  <div className="text-xs uppercase tracking-wider font-semibold opacity-70">
                    {c.label}
                  </div>
                  <div className="text-2xl font-bold mt-1">{c.value}</div>
                </div>
              ))}
            </div>

            {/* Order picker */}
            <div
              data-testid="partner-kit-order-picker"
              className="rounded-2xl border border-slate-200 bg-white p-5"
            >
              <h2 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" /> Order new boxes
              </h2>
              <div className="flex flex-wrap items-end gap-4">
                <label className="text-sm">
                  <span className="block text-slate-700 mb-1 font-medium">Boxes</span>
                  <input
                    type="number" min="1" max="100" step="1"
                    value={boxes}
                    onChange={(e) => setBoxes(Math.max(1, Math.min(100, parseInt(e.target.value || "1", 10))))}
                    data-testid="kit-order-boxes-input"
                    className="w-24 border border-slate-300 rounded-md h-10 px-3"
                  />
                </label>
                <div className="text-sm text-slate-700">
                  = <b data-testid="kit-order-kits-total">{kits}</b> kits ·
                  <b data-testid="kit-order-cents-total"> {CENTS(total)}</b>
                </div>
                <Button
                  onClick={checkout}
                  disabled={checkingOut}
                  data-testid="kit-order-checkout-btn"
                  className="bg-cyan-700 hover:bg-cyan-800 text-white h-10"
                >
                  {checkingOut ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DollarSign className="w-4 h-4 mr-2" />}
                  Pay {CENTS(total)} via Stripe
                </Button>
              </div>
              <div className="mt-4 flex gap-2 text-xs text-slate-500 items-start">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-none" />
                <span>
                  To receive your automatic <b>{CENTS(s.partner_kit_reimbursement_cents)}</b>{" "}
                  kit reimbursement, the customer must complete payment directly
                  through the AWARE online booking system.
                </span>
              </div>
            </div>

            {/* Order history */}
            <div className="rounded-2xl border border-slate-200 bg-white">
              <div className="p-4 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-900">Order history</h2>
                <Button variant="outline" size="sm" onClick={reload} data-testid="kit-orders-refresh-btn">
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
                </Button>
              </div>
              {orders.length === 0 ? (
                <div className="p-6 text-center text-slate-500 text-sm">
                  No orders yet.
                </div>
              ) : (
                <table className="w-full text-sm" data-testid="kit-orders-table">
                  <thead className="text-xs text-slate-500 uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-2">Order #</th>
                      <th className="text-left px-4 py-2">Boxes / Kits</th>
                      <th className="text-left px-4 py-2">Total</th>
                      <th className="text-left px-4 py-2">Status</th>
                      <th className="text-left px-4 py-2">Ordered</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o) => (
                      <tr key={o.id} className="border-t border-slate-100">
                        <td className="px-4 py-2 font-mono text-xs">{o.order_number}</td>
                        <td className="px-4 py-2">{o.boxes} / {o.kits}</td>
                        <td className="px-4 py-2">{CENTS(o.total_cents)}</td>
                        <td className="px-4 py-2"><StatusPill status={o.status} /></td>
                        <td className="px-4 py-2 text-slate-500">{(o.created_at || "").slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </PartnerShell>
  );
}

function StatusPill({ status }) {
  const map = {
    pending_payment:   { bg: "bg-slate-100",   text: "text-slate-700",   label: "Awaiting payment" },
    awaiting_labels:   { bg: "bg-amber-100",   text: "text-amber-800",   label: "Paid" },
    labels_generated:  { bg: "bg-sky-100",     text: "text-sky-800",     label: "Labels ready" },
    labels_failed:     { bg: "bg-rose-100",    text: "text-rose-800",    label: "Labels failed" },
    packing:           { bg: "bg-indigo-100",  text: "text-indigo-800",  label: "Packing" },
    shipped:           { bg: "bg-emerald-100", text: "text-emerald-800", label: "Shipped" },
    delivered:         { bg: "bg-emerald-100", text: "text-emerald-800", label: "Delivered" },
    cancelled:         { bg: "bg-slate-100",   text: "text-slate-500",   label: "Cancelled" },
    refunded:          { bg: "bg-rose-100",    text: "text-rose-700",    label: "Refunded" },
    failed:            { bg: "bg-rose-100",    text: "text-rose-800",    label: "Failed" },
  };
  const p = map[status] || map.pending_payment;
  return (
    <span data-testid={`kit-order-status-${status}`} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${p.bg} ${p.text}`}>
      {p.label}
    </span>
  );
}
